import React, { useCallback, useMemo, useRef } from "react";
import { BarRounded } from "@visx/shape";
import { Group } from "@visx/group";
import { GridRows } from "@visx/grid";
import { scaleBand, scaleLinear } from "@visx/scale";
import { TooltipWithBounds, defaultStyles, useTooltip } from "@visx/tooltip";
import { animated } from "react-spring";
import { AnimatedAxis } from "@visx/react-spring";
import { Flex, Text } from "@chakra-ui/react";
import {
  backgroundGrey,
  color1,
  color2,
  color3,
  color4,
  color5,
  color6,
  textSecondary,
  white,
} from "../../../../utils/colors";
import MonthlyItem from "../../../../components/charts/MonthlyItem";
import dayjs from "dayjs";
import localeData from "dayjs/plugin/localeData";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import useThemeColors from "../../../../hooks/useThemeColors";
import { formatNumberWithCommas } from "../../../../utils/formatUtils";
import useCurrencySymbol from "../../../../hooks/useCurrencySymbol";

dayjs.extend(localeData);

const defaultMargin = { top: 20, right: 0, bottom: 20, left: 45 };

// accessors

const getMonth = (d) => d.month;

const TreatmentPlansMonthlyChart = ({
  width,
  height,
  showFullYear,
  monthlyTreatmentPlans,
  showInPrice,
  currency,
}) => {
  const { t } = useTranslation();

  // COLORS
  const { textPrimaryMode, backgroundGreyMode } = useThemeColors();

  //REF
  const chartRef = useRef(null);

  // STATE
  const currencySymbol = useCurrencySymbol(currency);

  // TOOLTIP
  const {
    hideTooltip,
    showTooltip,
    tooltipData,
    tooltipLeft = 0,
    tooltipOpen,
    tooltipTop = 0,
  } = useTooltip({
    tooltipOpen: false,
  });

  // Define statuses and assign colors
  const statusColors = {
    planning: color2,
    approved: color3,
    ongoing: color4,
    completed: color5,
    cancelled: color6,
  };

  // bounds
  const xMax = width - defaultMargin.right - defaultMargin.left;
  const yMax = height - defaultMargin.top - defaultMargin.bottom;

  // Replace the current monthsToShow logic:
  const monthsToShow = showFullYear
    ? monthlyTreatmentPlans
    : monthlyTreatmentPlans.slice(0, 6);

  const getMonthLabel = (monthNum) => {
    return dayjs()
      .month(monthNum - 1)
      .format("MMM"); // e.g., 'Jan', 'Feb', etc.
  };

  const getMonthFullLabel = (monthNum) => {
    return dayjs()
      .month(monthNum - 1)
      .format("YYYY MMMM"); // e.g., 'January', 'February', etc.
  };
  const monthsToShowFormatted = monthsToShow.map((item) => ({
    ...item,
    month: getMonthLabel(item.month),
    fullMonth: getMonthFullLabel(item.month),
    totalMonthlyPrice: item.totalMonthlyPrice, // Add total monthly service price
    totalCompletedMonthlyPrice: item.totalCompletedMonthlyPrice, // Add total completed service price
    totalPlanCount: item.totalPlanCount, // Add total plan count
  }));

  const isAllMonthsZero = monthsToShowFormatted.every(
    (month) => month.totalPlanCount === 0 // Check if all months have zero plans
  );

  // Use `monthsToShowFormatted` for real data:
  const xScale = useMemo(
    () =>
      scaleBand({
        range: [0, xMax],
        round: true,

        domain: monthsToShowFormatted.map(getMonth), // Use real months from the API
        padding: 0.369,
      }),
    [xMax, monthsToShowFormatted]
  );
  let maxPrice = 0;
  let maxPlans = 0;

  // Y scale (totalMonthlyPrice)
  // Adjust the yScale for integer-only values
  const yScale = useMemo(() => {
    if (showInPrice) {
      maxPrice = Math.max(
        ...monthsToShowFormatted.map((d) => d.totalMonthlyPrice)
      );
    } else {
      maxPlans = Math.max(
        ...monthsToShowFormatted.map((d) =>
          d.statuses.reduce(
            (sum, status) => sum + status.totalTreatmentPlans,
            0
          )
        )
      );
    }

    return scaleLinear({
      range: [yMax, 0],
      round: true,
      nice: true, // Ensure clean rounding
      domain: isAllMonthsZero
        ? [0, 0]
        : [0, showInPrice ? maxPrice : Math.ceil(maxPlans) + 1],
    });
  }, [yMax, monthsToShowFormatted, showInPrice]);

  const handleTooltipShow = useCallback(
    (e, monthData) => {
      const { current } = chartRef;

      if (current && monthData !== undefined) {
        const bounding = current.getBoundingClientRect();
        showTooltip({
          tooltipData: monthData, // Pass the entire month data here
          tooltipLeft: e.clientX - bounding.x,
          tooltipTop: e.clientY - bounding.y,
        });
      }
    },
    [showTooltip]
  );

  const AnimatedBar = animated(BarRounded);

  // USE EFFECT

  // This function will return true if the current status is the top-most
  const isTopMostBar = (statuses, idx) => {
    // Check from the current index upwards to see if there are more statuses with non-zero values
    for (let i = idx + 1; i < statuses.length; i++) {
      if (statuses[i].totalTreatmentPlans > 0) {
        return false; // Found a higher non-zero bar, so this one is not the top-most
      }
    }
    return true; // This is the top-most bar
  };

  const isBottomMostBar = (statuses, idx) => {
    // Check from the current index downwards to see if there are more statuses with non-zero values
    for (let i = idx - 1; i >= 0; i--) {
      if (statuses[i].totalTreatmentPlans > 0) {
        return false; // Found a lower non-zero bar, so this one is not the bottom-most
      }
    }
    return true; // This is the bottom-most bar
  };

  // RETURN
  if (width < 10) {
    return null;
  }

  // // LOGS

  // console.log("monthlyTreatmentPlans:", monthlyTreatmentPlans);
  // console.log("monthsToShowFormatted:", monthsToShowFormatted);
  // console.log("showInPrice:", showInPrice);

  // console.log("isAllMonthsZero:", isAllMonthsZero);

  return (
    <>
      <Flex
        w="100%"
        position="relative"
        ref={chartRef}
        as={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: 0.2 } }}
      >
        <svg width={width} height={height} x={0} y={0}>
          <GridRows
            scale={yScale}
            width={xMax}
            strokeDasharray="6"
            stroke={backgroundGreyMode}
            left={defaultMargin.left}
            top={defaultMargin.top}
          />

          <Group top={defaultMargin.top} left={defaultMargin.left}>
            {monthsToShowFormatted.map((d, monthIdx) => {
              const month = getMonth(d);
              const barWidth = xScale.bandwidth();
              let cumulativeY = yMax; // Start from the bottom of the chart

              return (
                <Group key={`bar-group-${monthIdx}`}>
                  {d.statuses.map((status, idx) => {
                    let barHeight = 0;

                    if (showInPrice) {
                      barHeight = yMax - yScale(status.totalPrice);
                    } else {
                      barHeight = yMax - yScale(status.totalTreatmentPlans);
                    }

                    // Skip rendering if no treatment plans exist for this status
                    if (status.totalTreatmentPlans === 0) return null;

                    const barX = xScale(month);
                    cumulativeY -= barHeight; // Adjust y-position for stacking

                    // Check if the current bar is the top-most in this stack
                    const isTopMost = isTopMostBar(d.statuses, idx);
                    const isBottomMost = isBottomMostBar(d.statuses, idx);

                    // console.log("isTopMost:", isTopMost);

                    return (
                      <AnimatedBar
                        key={`bar-${monthIdx}-${idx}-${status.status}`}
                        radius={8}
                        top={isTopMost ? 8 : 0}
                        bottom={isBottomMost ? 8 : 0}
                        x={barX}
                        y={cumulativeY} // Correct the y position for stacking
                        width={barWidth}
                        height={barHeight} // Unique height for each bar based on its value
                        fill={statusColors[status.status]} // Apply the correct color
                        onMouseMove={(e) => handleTooltipShow(e, d)} // Pass the entire month data `d` here
                        onMouseLeave={() => hideTooltip()}
                      />
                    );
                  })}
                </Group>
              );
            })}
          </Group>

          <AnimatedAxis
            scale={yScale}
            left={defaultMargin.left}
            top={defaultMargin.top}
            // numTicks={6}
            stroke={backgroundGreyMode}
            orientation="left"
            tickLabelProps={() => ({
              fill: textSecondary,
              fontSize: "12px",
              dx: "-36px",
              dy: "3px",
            })}
            hideTicks
            numTicks={
              isAllMonthsZero ? 1 : showInPrice ? 6 : Math.min(10, maxPlans)
            }
            // numTicks={maxPlans <= 10 ? maxPlans : 10}
            // numTicks={Math.ceil(maxPlans)} // Set numTicks to the max whole number value
            tickFormat={(value) =>
              showInPrice
                ? formatNumberWithCommas(
                    value >= 1000 ? `${value / 1000}k` : value
                  )
                : Math.floor(value)
            }
            // hideAxisLine
            // hideZero
          />
          <AnimatedAxis
            top={yMax + defaultMargin.top}
            left={defaultMargin.left}
            scale={xScale}
            stroke={backgroundGreyMode}
            hideTicks
            tickLabelProps={() => ({
              fill: textSecondary,
              dx: "-12px",
              dy: "6px",
              fontSize: "12px",
            })}
            // ensures each series has a label
            numTicks={monthsToShowFormatted.length}
            orientation="bottom"
          />
        </svg>
        {tooltipData && tooltipOpen && (
          <TooltipWithBounds
            left={tooltipLeft}
            top={tooltipTop}
            style={{
              ...defaultStyles,
              boxShadow: "0px 2px 5px rgba(27, 47, 55, 0.2)",
              padding: "0.5rem 1rem",
              borderRadius: "1rem",
              backgroundColor: "#2D3748",
              color: "white",
              fontSize: "0.8rem",
              fontWeight: "500",
              whiteSpace: "nowrap",
            }}
          >
            <Flex align="flex-start" justify="center" direction="column">
              <Text
                fontSize="0.8rem"
                fontWeight="500"
                textTransform="capitalize"
                m="0 0 0.5rem"
              >
                {tooltipData.fullMonth} -{" "}
                <Text as="span" fontSize="xs">
                  {tooltipData.totalPlanCount} {t("dashboard.plans")}{" "}
                </Text>
              </Text>
              {tooltipData.statuses.map((status) => (
                <Flex
                  key={status.status}
                  align="flex-start"
                  justify="space-between"
                  my="4px"
                >
                  <Flex align="center" minW="100px">
                    <Flex
                      w="6px"
                      h="6px"
                      borderRadius="50%"
                      bg={statusColors[status.status]}
                      mr={1.5}
                    />
                    <Text
                      fontSize="xs"
                      fontWeight="500"
                      // color={statusColors[status.status]}
                      color={white}
                    >
                      {t(`treatmentPlanStatus.${status.status}`)}
                    </Text>
                  </Flex>
                  <Flex align="center" justify="flex-end">
                    <Text fontSize="xs" fontWeight="600" textAlign="right">
                      {currencySymbol}{" "}
                      {formatNumberWithCommas(status.totalPrice)}
                      <Text as="span" fontWeight="500" ml={1}>
                        ({status.totalTreatmentPlans})
                      </Text>
                    </Text>
                  </Flex>
                </Flex>
              ))}
            </Flex>
          </TooltipWithBounds>
        )}
      </Flex>

      <Flex direction="column" mt={4}>
        {monthsToShowFormatted
          .slice() // Create a copy of the array to avoid modifying the original
          .reverse() // Reverse the array to show the last month first
          .map((monthData, idx) => (
            <MonthlyItem
              key={`monthly-item-${monthData.month}-${idx}`}
              left={monthData.fullMonth}
              isPrice
              right={
                <Flex align="center" justify="flex-end" gap="1rem">
                  <Flex align="center">
                    <Text
                      fontSize="0.8rem"
                      color={textPrimaryMode}
                      fontWeight="500"
                      whiteSpace="nowrap"
                      // bg={textPrimary}
                      textAlign="right"
                    >
                      {monthData.totalPlanCount}
                    </Text>
                    <Text as="span" color={textSecondary} ml="0.25rem">
                      {t("dashboard.plans")}
                    </Text>
                  </Flex>

                  <Text
                    fontSize="0.8rem"
                    color={textPrimaryMode}
                    fontWeight="500"
                    whiteSpace="nowrap"
                    minW="6rem"
                    // bg={primary}
                    textAlign="right"
                  >
                    {currencySymbol} {/* {monthData.totalMonthlyPrice} € */}
                    {formatNumberWithCommas(
                      monthData.totalMonthlyPrice.toFixed(0)
                    )}{" "}
                  </Text>
                </Flex>
              }
              // isPrice
            />
          ))}
      </Flex>
    </>
  );
};

export default TreatmentPlansMonthlyChart;
